import { type ChangeEvent } from "react";
import NextLink from "next/link";
import {
  Loader,
  Box,
  Select,
  Button,
  Heading,
  IconFa,
} from "@cruk/cruk-react-components";
import { useRouter } from "next/compat/router";
import { faTrophy } from "@fortawesome/free-solid-svg-icons";

import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { fwsUrSuperTeamTeamPages } from "@fwa/src/services/superTeamService";
import { useTracking } from "@fwa/src/hooks/useTracking";
import { queryAsString } from "@fwa/src/utils/urlUtils";

import { MemberTeamJoin } from "@fwa/src/components/MemberTeamJoin";

import sharedStyles from "@fwa/src/components/styles.module.css";

import type {
  TeamPageType,
  SuperTeamPageType,
  SuperTeamMemberSortType,
  FwsResponseData,
} from "@fwa/src/types";

const MAX_ITEMS = 5;

export const SuperTeamTeamsOverview = ({
  page,
}: {
  page: SuperTeamPageType;
}) => {
  const { trackError } = useTracking();
  const router = useRouter();
  const { sort: sortQuery } = router?.query || {};
  const sort = (queryAsString(sortQuery) ||
    "amount") as SuperTeamMemberSortType;
  const url = page
    ? fwsUrSuperTeamTeamPages({
        teamId: page.uniqueId,
        currentPage: 1,
        itemsPerPage: MAX_ITEMS,
        sort,
      })
    : null;
  const { data, error, totalItems } = useSwrGet<FwsResponseData<TeamPageType>>(
    url,
    {},
  );
  const teams = data?.results || [];
  const showMoreLink = totalItems > MAX_ITEMS;

  const setSortInQueryString = (sortValue: string) => {
    router
      ?.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, sort: sortValue },
        },
        undefined,
        { shallow: true },
      )
      .catch((err: Error) => {
        trackError(err, {
          component: "SuperTeamTeamsOverview",
          function: "setSortInQueryString",
        });
      });
  };

  const handleFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const sortValue = e.target.value as SuperTeamMemberSortType;
    if (!sortValue) return;
    setSortInQueryString(sortValue || null);
  };

  return (
    <>
      {/* Loading State */}
      {!error && !data ? (
        <Box backgroundColor="background-light" padding="s">
          <Loader />
        </Box>
      ) : null}

      {/* Data State */}
      {teams.length ? (
        <Box
          data-component="super-team-teams-overview"
          backgroundColor="background-light"
          padding="s"
        >
          <Heading h3>Teams</Heading>
          <Box marginBottom="m">
            <NextLink
              href={`/unite${page.url.slice(
                page.url.lastIndexOf("/"),
              )}/leaderboardteams`}
              data-cta-type="link-teams-leaderboard"
            >
              <Button appearance="secondary" as="span">
                <IconFa faIcon={faTrophy} />
                Show leaderboard
              </Button>
            </NextLink>
          </Box>
          <Box>
            <div style={{ maxWidth: "26em" }}>
              <Select
                required
                value={sort}
                label="Sort Teams by"
                onChange={handleFilterChange}
                aria-controls="teamsList"
                hideRequiredIndicationInLabel
              >
                <option value="amount">amount raised</option>
                <option value="alpha">A-Z</option>
                <option value="recent">recently joined</option>
              </Select>
            </div>
          </Box>
          <ul
            className={sharedStyles["member-list"]}
            id="teamsList"
            aria-live="assertive"
            data-component="super-team-teams-list"
          >
            {!error &&
              teams
                .filter((teamPage: TeamPageType) => teamPage.uniqueId)
                .map((teamPage: TeamPageType) => (
                  <li key={teamPage.uniqueId}>
                    <MemberTeamJoin teamPage={teamPage} />
                  </li>
                ))}
          </ul>
          {showMoreLink && (
            <Box>
              <Box marginTop="s">
                <NextLink
                  href={`/unite${page?.url?.slice(
                    page.url.lastIndexOf("/"),
                  )}/teams`}
                  data-cta-type="link-all-teams"
                >
                  <Button
                    as="span"
                    appearance="secondary"
                  >{`See all Teams (${totalItems})`}</Button>
                </NextLink>
              </Box>
            </Box>
          )}
        </Box>
      ) : null}
    </>
  );
};

export default SuperTeamTeamsOverview;
